/**
 * ApexCharts Theme Configuration
 * Maps Tailwind CSS variables to ApexCharts theme
 */

export const getApexTheme = () => {
  const isDark = document.documentElement.classList.contains('dark');
  const style = getComputedStyle(document.documentElement);
  
  // Extract HSL values and convert to hex
  const getHSLColor = (variable: string): string => {
    const hsl = style.getPropertyValue(variable).trim();
    if (!hsl) return isDark ? '#ffffff' : '#000000';
    
    // Parse HSL values (e.g., "222.2 47.4% 11.2%")
    const [h, s, l] = hsl.split(' ').map(v => parseFloat(v.replace('%', '')));
    return hslToHex(h, s, l);
  };

  return {
    theme: {
      mode: isDark ? 'dark' : 'light' as 'dark' | 'light',
      palette: 'palette1'
    },
    chart: {
      background: 'transparent',
      foreColor: getHSLColor('--foreground'),
      fontFamily: 'inherit',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    colors: [
      getHSLColor('--primary'),
      getHSLColor('--chart-2'),
      getHSLColor('--chart-3'),
      getHSLColor('--chart-4'),
      getHSLColor('--chart-5'),
    ],
    grid: {
      borderColor: getHSLColor('--border'),
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 10
      }
    },
    stroke: {
      width: 2,
      curve: 'smooth' as const
    },
    dataLabels: {
      enabled: false
    },
    markers: {
      size: 0,
      hover: {
        size: 6
      }
    },
    xaxis: {
      axisBorder: {
        color: getHSLColor('--border')
      },
      axisTicks: {
        color: getHSLColor('--border')
      },
      labels: {
        style: {
          colors: getHSLColor('--muted-foreground'),
          fontSize: '12px'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: getHSLColor('--muted-foreground'),
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: {
        fontSize: '12px'
      },
      x: {
        show: true
      },
      y: {
        formatter: undefined
      }
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'left' as const,
      fontSize: '12px',
      fontWeight: 500,
      labels: {
        colors: getHSLColor('--foreground')
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0
      }
    }
  };
};

// Helper function to convert HSL to Hex
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
